import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Rocket, ArrowRight, ArrowLeft } from 'lucide-react';
import api from '../lib/api';

// Bug #21 — the "seen" state is DB-backed (per user) so the tour never re-opens
// in incognito / a new browser / after localStorage is cleared. localStorage is
// kept only as a fast-path cache to avoid a flash before the API answers.
const SEEN_KEY = 'crm_first_login_tour_v1';
const FLAG = 'first_login_tour_seen';
// Only genuinely first-time users (onboarding completed within this window) see
// the welcome tour. Established accounts are silently suppressed so it never nags.
const FRESH_ONBOARD_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Plan/role-aware first-login walkthrough. A lightweight modal (no DOM
 * spotlighting, so it can never point at a missing element) with a GUARANTEED
 * Skip. Slides are filtered by plan/role so users only see relevant steps.
 */
export default function FirstLoginTour({ planTier = 'free', isAdmin = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  const paid = planTier && planTier !== 'free';

  const slides = [
    {
      title: 'Welcome to Lockene 👋',
      body: 'Your AI sales workspace. Here is a 30-second tour of where things live — you can skip anytime.',
    },
    {
      title: 'Your pipeline lives in Leads',
      body: 'Import contacts, work your Kanban board, reveal verified emails/phones, and export any filtered view to CSV.',
      cta: { label: 'Open Leads', href: '/leads' },
    },
    paid && {
      title: 'Reach people with Campaigns',
      body: 'Launch Voice, Email or WhatsApp campaigns to an audience — set the pace and press play.',
      cta: { label: 'Open Campaigns', href: '/campaigns' },
    },
    paid && {
      title: 'Automate LinkedIn safely',
      body: 'Queue connection requests and messages from the LinkedIn Outbox, sent human-like within safe daily limits.',
      cta: { label: 'LinkedIn Outbox', href: '/linkedin-outbox' },
    },
    isAdmin && {
      title: 'Set up your workspace',
      body: 'Connect email, invite teammates and configure plans under Account & Integrations.',
      cta: { label: 'Open Integrations', href: '/integrations' },
    },
    {
      title: 'Need help? It is always one tap away',
      body: 'Tap the “?” in the top-right corner any time to open Help & Best Practices with step-by-step guides.',
      cta: { label: 'Open Help Center', href: '/help' },
    },
  ].filter(Boolean);

  useEffect(() => {
    let cancelled = false;
    // Fast path: if this browser already recorded it, never show or call.
    try { if (localStorage.getItem(SEEN_KEY)) return undefined; } catch (_e) { /* ignore */ }

    const markSeen = () => {
      try { localStorage.setItem(SEEN_KEY, '1'); } catch (_e) { /* ignore */ }
      api.put('/api/profile/ui-flags', { key: FLAG, value: true }).catch(() => {});
    };

    // DB-backed truth + onboarding recency: only open for a GENUINELY first-time
    // user (onboarding just completed). Established/legacy accounts are silently
    // marked seen so the welcome tour never re-appears for them.
    Promise.all([
      api.get('/api/profile/ui-flags').then((r) => r.data).catch(() => null),
      api.get('/api/onboarding/status').then((r) => r.data).catch(() => null),
    ]).then(([flags, status]) => {
      if (cancelled) return;
      // Transient ui-flags error → stay conservative: do NOT open or persist
      // (a user who already dismissed it must never be nagged; they can replay
      // from Help → Replay tour).
      if (!flags) return;
      const seen = !!(flags.ui_flags && flags.ui_flags[FLAG]);
      if (seen) { try { localStorage.setItem(SEEN_KEY, '1'); } catch (_e) { /* ignore */ } return; }
      const completedAt = status && status.onboarding_completed_at;
      // Unknown completion time (legacy/transient) → don't open, don't persist.
      if (!completedAt) return;
      const isFreshOnboard = (Date.now() - new Date(completedAt).getTime()) <= FRESH_ONBOARD_MS;
      if (!isFreshOnboard) { markSeen(); return; }  // established account → suppress for good
      setTimeout(() => { if (!cancelled) setOpen(true); }, 1200);
    });
    return () => { cancelled = true; };
  }, []);

  // Replay-tour trigger (fired from the Help menu in the header).
  useEffect(() => {
    const replay = () => { setStep(0); setOpen(true); };
    window.addEventListener('lockene:replay-tour', replay);
    return () => window.removeEventListener('lockene:replay-tour', replay);
  }, []);

  const finish = () => {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch (_e) { /* noop */ }
    // Persist to the DB so it stays dismissed across browsers / incognito (Bug #21).
    api.put('/api/profile/ui-flags', { key: FLAG, value: true }).catch(() => {});
    setOpen(false);
  };

  const s = slides[step];
  const isLast = step === slides.length - 1;
  if (!s) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="sm:max-w-md" data-testid="first-login-tour">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Rocket className="h-5 w-5" />
          </div>
          <DialogTitle data-testid="tour-slide-title">{s.title}</DialogTitle>
          <DialogDescription className="pt-1 text-sm leading-relaxed">{s.body}</DialogDescription>
        </DialogHeader>

        {s.cta && (
          <Button
            variant="outline"
            className="w-full justify-between"
            onClick={() => { navigate(s.cta.href); finish(); }}
            data-testid="tour-slide-cta"
          >
            {s.cta.label} <ArrowRight className="h-4 w-4" />
          </Button>
        )}

        <div className="mt-2 flex items-center justify-center gap-1.5">
          {slides.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-5 bg-primary' : 'w-1.5 bg-muted'}`} />
          ))}
        </div>

        <DialogFooter className="mt-2 flex-row items-center justify-between gap-2 sm:justify-between">
          <Button variant="ghost" size="sm" onClick={finish} data-testid="tour-skip">
            Skip
          </Button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStep((v) => v - 1)} data-testid="tour-back">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {isLast ? (
              <Button size="sm" onClick={finish} data-testid="tour-finish">Get started</Button>
            ) : (
              <Button size="sm" onClick={() => setStep((v) => v + 1)} data-testid="tour-next">
                Next <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
